import React from 'react';
import { X, Loader2, Clock } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { formatDateHuman } from './prestationPlanningShared';

interface Suggestion { date: string; start_time: string; adjacent: boolean; employees: { id: string; nom: string }[] }

const DUREES = [30, 60, 90, 120, 180, 240, 360];
const libelleDuree = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ''}`);

/**
 * « Trouver un créneau » (2026-10-18, audit concurrents : Jobber). On indique la durée de la prestation et,
 * si on veut, qui doit la faire ; on obtient les meilleurs créneaux des prochains jours. Un clic ouvre le
 * nouveau rendez-vous avec l'heure et le salarié déjà remplis.
 */
export function TrouverCreneauModal({ employees, onClose, onPick }: {
    employees: { id: string; first_name?: string; last_name?: string }[];
    onClose: () => void;
    onPick: (s: { date: string; start_time: string; employee_ids: string[] }) => void;
}) {
    const [duree, setDuree] = React.useState(60);
    const [salarieId, setSalarieId] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [resultats, setResultats] = React.useState<Suggestion[] | null>(null);

    const chercher = async () => {
        setLoading(true); setErreur(''); setResultats(null);
        try {
            const res: any = await moduleApi.findPrestationSlot({ duration_minutes: duree, employee_ids: salarieId ? [salarieId] : undefined, days: 21 });
            if (res?.error) throw new Error(res.error);
            setResultats(Array.isArray(res?.suggestions) ? res.suggestions : []);
        } catch (e: any) { setErreur(e?.message || 'Recherche impossible.'); }
        finally { setLoading(false); }
    };

    const champ = 'bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500';
    // Regroupement par jour, dans l'ordre des dates.
    const parJour: Record<string, Suggestion[]> = {};
    (resultats || []).forEach((s) => { (parJour[s.date] = parJour[s.date] || []).push(s); });

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-black text-content flex items-center gap-2"><Clock size={18} className="text-brand-600 dark:text-brand-400" /> Trouver un créneau</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer" className="p-1.5 rounded-lg text-content-muted hover:text-content"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-bold text-content-muted">Durée de la prestation
                        <select className={`${champ} block w-full mt-1`} value={duree} onChange={(e) => setDuree(Number(e.target.value))}>
                            {DUREES.map((d) => <option key={d} value={d}>{libelleDuree(d)}</option>)}
                        </select>
                    </label>
                    <label className="text-xs font-bold text-content-muted">Salarié
                        <select className={`${champ} block w-full mt-1`} value={salarieId} onChange={(e) => setSalarieId(e.target.value)}>
                            <option value="">Peu importe</option>
                            {employees.map((s) => <option key={s.id} value={s.id}>{`${s.first_name || ''} ${s.last_name || ''}`.trim()}</option>)}
                        </select>
                    </label>
                </div>
                <button type="button" onClick={chercher} disabled={loading} className="w-full px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                    {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Chercher'}
                </button>
                {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
                {resultats && resultats.length === 0 && <p className="text-sm text-content-muted text-center">Aucun créneau libre dans les trois prochaines semaines pour cette durée.</p>}
                {Object.entries(parJour).map(([jour, liste]) => (
                    <div key={jour} className="space-y-1.5">
                        <p className="text-[11px] font-black uppercase tracking-widest text-content-muted">{formatDateHuman(jour)}</p>
                        <div className="flex flex-wrap gap-2">
                            {liste.map((s) => (
                                <button key={s.date + s.start_time} type="button" onClick={() => onPick({ date: s.date, start_time: s.start_time, employee_ids: s.employees.slice(0, 1).map((e) => e.id) })}
                                    title={s.employees.length ? `Libres : ${s.employees.map((e) => e.nom).join(', ')}` : ''}
                                    className="text-left px-3 py-2 rounded-xl border border-border bg-surface-soft hover:border-brand-500 transition-colors">
                                    <span className="block text-sm font-black text-content">{s.start_time}</span>
                                    <span className="block text-[11px] text-content-muted">{s.employees.length ? s.employees.slice(0, 2).map((e) => e.nom).join(', ') + (s.employees.length > 2 ? ` +${s.employees.length - 2}` : '') : 'À assigner'}</span>
                                    {s.adjacent && <span className="block text-[10px] font-bold text-emerald-600 dark:text-emerald-400">Sans trou dans la journée</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
